import { t } from '@lingui/core/macro';
import { InspectionContext, type InspectionSessionRecord, TireSeason } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { BrandType } from 'noddi-types';
import { NoddiIcon } from 'noddi-ui-common';
import { ApiErrorMessageInternal, NoddiEmptyContent } from 'noddi-ui-web';
import {
  isWheelSetCarWheelMeasurementValues,
  mapInspectionSessionsToMeasurements,
  TireMeasurementCard,
  treadDepthThresholdsFromLatest
} from 'noddi-web';
import { getCommonTranslations } from '../../utils/commonTranslations';
import { getBrandSpecificContent } from './getBrandSpecificContent';
import { TireInformationButton } from './TireInformationButton';
import { WheelInspection } from './WheelInspection';

interface CarInspectionAndMeasurementProps {
  carId: number;
  sessions: InspectionSessionRecord[];
  brandDomain: BrandType;
}

export const CarInspectionAndMeasurement = ({ carId, sessions, brandDomain }: CarInspectionAndMeasurementProps) => {
  const carWheelMeasurements = mapInspectionSessionsToMeasurements(sessions);
  // Sessions carry the readings; the legal and recommended minimums come from the car's country.
  const { data: treadDepthLatest, error: thresholdsError } = noddiAsync.useGet({
    type: URLKeys.getLatestCarWheelMeasurements,
    input: { carId }
  });

  const hasSummerTireMeasurements = Object.keys(carWheelMeasurements.summer).length > 0;
  const hasWinterTireMeasurements = Object.keys(carWheelMeasurements.winter).length > 0;
  const hasTireMeasurements = hasSummerTireMeasurements || hasWinterTireMeasurements;

  const hasInspection = sessions.some(
    (session) => session.context === InspectionContext.PRE_JOB || session.context === InspectionContext.POST_JOB
  );

  const items = getBrandSpecificContent(brandDomain);
  if (!hasTireMeasurements && !hasInspection) {
    return (
      <div>
        <NoddiEmptyContent title={t`What will info be available here?`}>
          <ul className='-mt-4 flex flex-col gap-3'>
            {items.map((item, index) => (
              <li key={index} className='flex gap-1.5 text-start text-balance'>
                <NoddiIcon name='Check' size='small' className='mt-0.5 shrink-0 text-emerald-500' />
                <span className='text-sm'>{item}</span>
              </li>
            ))}
          </ul>
        </NoddiEmptyContent>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      {thresholdsError && <ApiErrorMessageInternal error={thresholdsError} />}
      {hasTireMeasurements && (
        <>
          <TireInformationButton />
          <div className='grid grid-cols-1 gap-4 2xl:grid-cols-2'>
            {hasSummerTireMeasurements && isWheelSetCarWheelMeasurementValues(carWheelMeasurements.summer) && (
              <TireMeasurementCard
                tireSeason={TireSeason.SUMMER}
                thresholds={treadDepthThresholdsFromLatest(treadDepthLatest, TireSeason.SUMMER)}
                measurements={carWheelMeasurements.summer}
                translations={getCommonTranslations().wheelMeasurements}
              />
            )}
            {hasWinterTireMeasurements && isWheelSetCarWheelMeasurementValues(carWheelMeasurements.winter) && (
              <TireMeasurementCard
                tireSeason={TireSeason.WINTER}
                thresholds={treadDepthThresholdsFromLatest(treadDepthLatest, TireSeason.WINTER)}
                measurements={carWheelMeasurements.winter}
                translations={getCommonTranslations().wheelMeasurements}
              />
            )}
          </div>
        </>
      )}

      {hasInspection && <WheelInspection sessions={sessions} />}
    </div>
  );
};
